import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { ResponseWrapper } from "@/services/response_wrapper";
import { ErrorWrapper } from "@/services/error_wrapper";
import { getPathAPI } from "@/helpers";
import { getAuthorizationBearerStringFor } from "@/helpers/auth-headers";
const { version } = require("../../package.json");

export { getAuthorizationBearerStringFor };

const DB_URL = getPathAPI();

interface RequestOptions {
  useToken?: string;
  contentType?: string;
  retry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

export default class BaseService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: DB_URL,
      timeout: 60000 // 30 second timeout
    });
  }

  async request(
    axiosConfig: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ResponseWrapper> {
    const requestOptions: RequestOptions = {
      retry: true,
      maxRetries: 2,
      retryDelay: 1000,
      ...options
    };

    return this.executeWithRetry(axiosConfig, requestOptions, 0);
  }

  private async executeWithRetry(
    axiosConfig: AxiosRequestConfig,
    options: RequestOptions,
    attempt: number
  ): Promise<ResponseWrapper> {
    try {
      const perRequestHeaders: Record<string, string> = {
        ...(axiosConfig.headers || {}),
        "X-Atlas-App-Version": version
      };

      if (options.useToken) {
        const authHeader = getAuthorizationBearerStringFor(options.useToken);
        if (authHeader) {
          perRequestHeaders["Authorization"] = authHeader;
        } else {
          this.throwIfInvalidToken(axiosConfig);
        }
      }
      if (options.contentType) {
        perRequestHeaders["Content-Type"] = options.contentType;
      }

      const response = await this.instance.request({
        ...axiosConfig,
        headers: perRequestHeaders
      });
      return new ResponseWrapper(response);
    } catch (e) {
      const errorWrapper = new ErrorWrapper(e as any);

      // Determine if we should retry
      const shouldRetry = this.shouldRetryRequest(
        errorWrapper,
        options,
        attempt
      );

      if (shouldRetry) {
        // Wait before retrying with exponential backoff
        const delay = options.retryDelay! * Math.pow(2, attempt);
        await this.delay(delay);

        return this.executeWithRetry(axiosConfig, options, attempt + 1);
      }

      throw errorWrapper;
    }
  }

  private throwIfInvalidToken(
    axiosConfig?: AxiosRequestConfig,
    message?: any
  ): never {
    // If token is invalid, create an AxiosError-like object for consistent error handling
    let error: any;
    if (message) {
      error = new Error(`Invalid token: ${message}`);
    } else {
      error = new Error("Invalid or missing token");
    }

    error.response = {
      status: 401,
      data: {
        response: {
          success: false,
          message: "Invalid Token"
        }
      }
    };
    error.config = axiosConfig;
    throw error;
  }

  private shouldRetryRequest(
    error: ErrorWrapper,
    options: RequestOptions,
    attempt: number
  ): boolean {
    // Don't retry if retry is disabled or we've exceeded max retries
    if (!options.retry || attempt >= (options.maxRetries || 2)) {
      return false;
    }

    // Retry on network errors and timeouts
    if (error.isNetworkError || error.isTimeout) {
      return true;
    }

    // Retry on specific HTTP status codes
    if (typeof error.status === "number") {
      const retryableStatusCodes = [408, 429, 502, 503, 504];
      return retryableStatusCodes.includes(error.status);
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getInstance(): AxiosInstance {
    return this.instance;
  }
}
